import React, { useState, useEffect, useCallback, useRef } from 'react';
import { fetchUserByMobile, createServiceOrder, getModelList } from '../actions/action';
import {
  COMPLAINT_DELIVERY_DELAY_SUB_TYPES,
  COMPLAINT_DELIVERY_OTHERS_SUB_TYPES,
  COMPLAINT_INSTALLATION_ISSUE_SUB_TYPES,
  DELIVERY_COMPLAINT_TYPES,
  PRODUCT_COMPLAINT_TYPES,
  SERVICE_REQUEST_TYPES,
  SERVICE_REQUEST_WATER_PURIFIER,
} from '../../../../constants/complaintTypes';
import { useDropzone } from 'react-dropzone';
import { Offcanvas, OffcanvasBody, OffcanvasHeader, Spinner, Collapse } from 'reactstrap';
import toast from 'react-hot-toast';
import AddAddress from '../../../Orders/Cart/components/AddAddress';
import './CreateServiceOrderOffcanvas.css';

const initialFormState = {
  fullname: '',
  mobile: '',
  alternateMobile: '',
  email: '',
  serviceRequest: '',
  type: '',
  maintenanceType: '',
  qty: '',
  orderNo: '',
  remarks: '',
  files: [],
  modelNo: '',
  serialNo: '',
  building: '',
  street: '',
  flat_no: '',
  floor_no: '',
  area: '',
  landmark: '',
  company_name: '',
  trn_no: '',
  emirate: '',
  city: '',
  latitude: '',
  longitude: '',
};

/* Map sub-type display labels to API value slugs */
const SUB_TYPE_VALUE_MAP = {
  'Filter Change': 'filter_change',
  'Water Leakage': 'water_leakage',
  'Installation': 'installation',
  'Re-Installation': 'reinstallation',
  'AMC': 'amc',
  'Others': 'others',
};

/* Helper to get icon class based on type name */
const getIconClass = (typeName) => {
  if (!typeName) return 'bx bx-category';
  const lower = typeName.toLowerCase();
  
  // Water Purifier / Plumbing
  if (lower.includes('water') || lower.includes('filter') || lower.includes('leak') || lower.includes('plumbing')) return 'bx bx-droplet';
  
  // Gazebo / Outdoor
  if (lower.includes('gazebo') || lower.includes('roof') || lower.includes('panel') || lower.includes('garden')) return 'bx bx-home-alt';
  
  // Electrical / Lights
  if (lower.includes('light') || lower.includes('led') || lower.includes('electric') || lower.includes('power')) return 'bx bx-bulb';
  
  // Installation / Assembly / Fitting
  if (lower.includes('start') || lower.includes('install') || lower.includes('assembl') || lower.includes('fitting') || lower.includes('fix')) return 'bx bx-wrench';
  
  // Re Installation
  if (lower.includes('re-install')) return 'bx bx-refresh';
  
  // Delivery / Logistics
  if (lower.includes('delivery') || lower.includes('delay') || lower.includes('transport') || lower.includes('logis')) return 'bx bx-truck';
  
  // Damage / Defect / Broken
  if (lower.includes('damage') || lower.includes('scratch') || lower.includes('broken') || lower.includes('defect') || lower.includes('missing')) return 'bx bx-error-circle';
  
  // Maintenance / Repair / General
  if (lower.includes('maintenance') || lower.includes('repair') || lower.includes('service')) return 'bx bx-cog';
  
  // Others
  return 'bx bx-category';
};

const CreateServiceOrderOffcanvas = ({
  isOpen,
  toggle,
  users,
  selectedOrderData,
  onServiceOrderCreated,
}) => {
  // Removed mobile search state and logic
  const [isDataLoading, setDataLoading] = useState(false);
  const [form, setForm] = useState(initialFormState);
  const [models, setModels] = useState([]);
  const [isOpenAddress, setIsOpenAddress] = useState(false);
  const addressFormRef = useRef(null);

  const initialAddressData = React.useMemo(() => {
    if (!selectedOrderData) return null;
    return {
      buildingName: selectedOrderData.buildingName || selectedOrderData.Address || '',
      streetName: selectedOrderData.streetName || selectedOrderData.Address_2 || '',
      flatNo: selectedOrderData.flatNo || '',
      floorNumber: selectedOrderData.floorNumber || '',
      areaName: selectedOrderData.areaName || selectedOrderData.Area || '',
      nearestLandMark: selectedOrderData.nearestLandMark || '',
      companyName: selectedOrderData.company_name || '',
      trnNo: selectedOrderData.trn_no || '',
      latitude: selectedOrderData.Latitude || '',
      longitude: selectedOrderData.Longitude || '',
      region: selectedOrderData.Emirate || '',
      city: selectedOrderData.City || '',
    };
  }, [selectedOrderData]);

  const [customerData, setCustomerData] = useState(null);

  useEffect(() => {
    if (isOpen && !selectedOrderData?.model) {
      const fetchModels = async () => {
        try {
          const response = await getModelList();
          const rawData = Array.isArray(response?.data) ? response.data : (Array.isArray(response) ? response : []);
          if (rawData.length > 0) {
            // Extract unique names from the data
            const uniqueNames = [...new Set(rawData.map(item => item.name))].filter(Boolean);
            setModels(uniqueNames);
          }
        } catch (error) {
          console.error('Error fetching models:', error);
        }
      };
      fetchModels();
    }
  }, [isOpen, selectedOrderData?.model]);

  // Prefill mobile if passed as selectedOrderData.mobile (when proceeding to create after search)
  useEffect(() => {
    if (
      selectedOrderData &&
      isOpen &&
      !selectedOrderData.customer_name &&
      selectedOrderData.mobile
    ) {
      setForm((prev) => ({ ...prev, mobile: selectedOrderData.mobile }));
    }
  }, [selectedOrderData, isOpen]);

  // Pre-fill form when selectedOrderData is provided
  useEffect(() => {
    if (selectedOrderData && isOpen) {
      setCustomerData(selectedOrderData);
      setForm({
        ...initialFormState,
        fullname: selectedOrderData.customer_name || '',
        email: selectedOrderData.email || '',
        orderNo: selectedOrderData.so_no || '',
        mobile:
          selectedOrderData.Mobile_Number?.replace(/^(\+|00)?971/, '') ||
          selectedOrderData.mobile ||
          '',
        alternateMobile:
          selectedOrderData.Alternate_Mobile?.replace(/^(\+|00)?971/, '') ||
          selectedOrderData.alternate_mobile ||
          '',
        modelNo: selectedOrderData.model || '',
        serialNo: selectedOrderData.serial_no || selectedOrderData.serialNo || '',
        building: selectedOrderData.buildingName || selectedOrderData.Address || '',
        street: selectedOrderData.streetName || selectedOrderData.Address_2 || '',
        flat_no: selectedOrderData.flatNo || '',
        floor_no: selectedOrderData.floorNumber || '',
        area: selectedOrderData.areaName || selectedOrderData.Area || '',
        landmark: selectedOrderData.nearestLandMark || '',
        emirate: selectedOrderData.Emirate || '',
        city: selectedOrderData.City || '',
        latitude: selectedOrderData.Latitude || '',
        longitude: selectedOrderData.Longitude || '',
      });
    }
  }, [selectedOrderData, isOpen]);

  const handleAddressSet = (addressData) => {
    setForm(prev => ({
      ...prev,
      building: addressData.buildingName || '',
      street: addressData.streetName || '',
      flat_no: addressData.flatNo || '',
      floor_no: addressData.floorNumber || '',
      area: addressData.areaName || '',
      landmark: addressData.nearestLandMark || '',
      company_name: addressData.companyName || '',
      trn_no: addressData.trnNo || '',
      latitude: addressData.latitude || '',
      longitude: addressData.longitude || '',
      city: addressData.city || '',
      emirate: addressData.region || '',
    }));
  };



  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // File upload constraints
  const MAX_FILES = 5;
  const MAX_TOTAL_SIZE = 5 * 1024 * 1024; // 5MB
  const [fileError, setFileError] = useState('');

  // Drag and drop handler for files
  const onDrop = useCallback(
    (acceptedFiles) => {
      setFileError('');
      setForm((prev) => {
        const newFiles = [...prev.files, ...acceptedFiles];
        if (newFiles.length > MAX_FILES) {
          setFileError('You can upload a maximum of 5 images.');
          return prev;
        }
        const totalSize = newFiles.reduce((sum, file) => sum + file.size, 0);
        if (totalSize > MAX_TOTAL_SIZE) {
          setFileError('Total file size must be less than 5MB.');
          return prev;
        }
        return {
          ...prev,
          files: newFiles,
        };
      });
    },
    [MAX_FILES, MAX_TOTAL_SIZE],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    accept: 'image/jpeg, image/png, image/jpg',
    maxFiles: MAX_FILES,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Trigger local validation for inputs in AddAddress
    if (addressFormRef.current) {
      addressFormRef.current.submit();
    }

    // Mandatory Address Fields Validation
    const requiredAddressFields = {
      building: 'Apartment/Building Name',
      street: 'Street Name',
      flat_no: 'Flat No',
      floor_no: 'Floor Number',
      area: 'Area Name',
      landmark: 'Nearest Landmark',
    };

    const missingFields = Object.entries(requiredAddressFields)
      .filter(([key]) => !form[key] || typeof form[key] !== 'string' || !form[key].trim())
      .map(([, label]) => label);

    if (missingFields.length > 0) {
      toast.error(`Please fill mandatory address fields: ${missingFields.join(', ')}`, {
        duration: 4000,
        style: {
          border: '1px solid #f44336',
          padding: '16px',
          color: '#ffffff',
          background: '#f44336',
        },
      });
      setIsOpenAddress(true); // Automatically expand the accordion to show where the error is
      return;
    }

    // Map form fields to API fields
    const payload = {
      customer_name: form.fullname,
      country: 'ae', // You may want to make this dynamic
      mobile: form.mobile,
      alternate_mobile: form.alternateMobile,
      order_no: form.orderNo || '',
      remarks: form.remarks,
      qty: form.qty,
      product_type: form.type,
      service_type: SUB_TYPE_VALUE_MAP[form.maintenanceType] || form.maintenanceType,
      created_by: users?.user_name || '', // Set this from user context if available
      oms_user_id: users?.user_id || '', // Set this from user context if available
      user_empid: users?.salesPersonCode || '', // Set this from user context if available
      email: form.email,
      building: form.building,
      street: form.street,
      flat_no: form.flat_no,
      floor_no: form.floor_no,
      area: form.area,
      landmark: form.landmark,
      company_name: form.company_name,
      trn_no: form.trn_no,
      emirate: form.emirate,
      city: form.city,
      latitude: form.latitude,
      longitude: form.longitude,
      files: form.files,
      model_no: form.modelNo,
      serial_no: form.serialNo,
    };
    try {
      setDataLoading(true);
      const response = await createServiceOrder(payload);
      if (response?.data?.code === 100) {
        // toast.dismiss();
        toast.success('Service Order Created!', {
          duration: 2000,
          style: {
            border: '1px solid #fff',
            padding: '20px',
            color: '#ffffff',
            background: '#4CAF50',
          },
          iconTheme: {
            primary: '#FFFAEE',
            secondary: '#4CAF50',
          },
        });
        if (onServiceOrderCreated) onServiceOrderCreated();
        toggle();
      } else {
        toast.error(response?.data?.message, {
          duration: 2000,
          style: {
            border: '1px solid #fff',
            padding: '20px',
            color: '#ffffff',
            background: '#f44336',
          },
          iconTheme: {
            primary: '#FFFAEE',
            secondary: '#f44336',
          },
        });
      }
      setDataLoading(false);
    } catch (err) {
      toast.error('Failed to submit request.', {
        style: {
          border: '1px solid #fff',
          padding: '20px',
          color: '#ffffff',
          background: '#f44336',
        },
        iconTheme: {
          primary: '#FFFAEE',
          secondary: '#f44336',
        },
      });
    } finally {
      setDataLoading(false);
    }
  };

  // Reset form when offcanvas is closed
  useEffect(() => {
    if (!isOpen) {
      setForm(initialFormState);
    }
  }, [isOpen]);

  // Auto-select the default installation type from the constant list
  useEffect(() => {
    if (isOpen && !form.type && SERVICE_REQUEST_TYPES.length > 0) {
      setForm((prev) => ({ ...prev, type: SERVICE_REQUEST_TYPES[0] }));
    }
  }, [isOpen, form.type]);



  // Simple shimmer component
  const Shimmer = () => (
    <div style={{ padding: 16 }}>
      <div
        className="shimmer shimmer-line"
        style={{ height: 32, width: '40%', marginBottom: 16 }}
      />
      <div
        className="shimmer shimmer-line"
        style={{ height: 32, width: '60%', marginBottom: 16 }}
      />
      <div
        className="shimmer shimmer-line"
        style={{ height: 32, width: '80%', marginBottom: 16 }}
      />
      <div
        className="shimmer shimmer-line"
        style={{ height: 32, width: '50%', marginBottom: 16 }}
      />
    </div>
  );

  // Add shimmer CSS
  const shimmerStyle = `
.shimmer {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 37%, #f0f0f0 63%);
  background-size: 400% 100%;
  animation: shimmer 1.2s ease-in-out infinite;
  border-radius: 6px;
}
@keyframes shimmer {
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
}
`;
  if (
    typeof document !== 'undefined' &&
    !document.getElementById('shimmer-style')
  ) {
    const style = document.createElement('style');
    style.id = 'shimmer-style';
    style.innerHTML = shimmerStyle;
    document.head.appendChild(style);
  }



  return (
    <Offcanvas
      isOpen={isOpen}
      toggle={toggle}
      direction="end"
      className="create-service-order-offcanvas"
      style={{ width: '70vw', maxWidth: '80vw' }}
    >
      <OffcanvasHeader toggle={toggle}>Create Service Order</OffcanvasHeader>
      <OffcanvasBody>
        {/* Only show search if no selectedOrderData is provided */}
        {/* Only show the mobile search form if we haven't searched yet and no data is present */}
        {/* Always show the main service order form, never the mobile search */}
        <form onSubmit={handleSubmit}>
            {/* Customer & Order Information - Read-only when from search */}
            {selectedOrderData ? (
              <>
              <div className="mb-4">
                <div className="row g-4">
                  {/* Left Column: Customer Information */}
                  <div className="col-md-6">
                    <div className="info-card customer">
                      <div className="card-title">
                        <i className="bx bx-user me-2"></i>Customer Information
                      </div>
                      <div className="mb-3">
                        <label className="form-label">CUSTOMER NAME</label>
                        <input
                          type="text"
                          className="form-control"
                          name="fullname"
                          value={form.fullname}
                          onChange={handleChange}
                          placeholder="Enter customer name"
                          required
                          disabled={!!selectedOrderData.customer_name}
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">MOBILE NUMBER</label>
                        <div className="input-group">
                          <span className="input-group-text">+971</span>
                          <input
                            type="text"
                            className="form-control"
                            name="mobile"
                            value={form.mobile}
                            onChange={(e) => {
                              const val = e.target.value.replace(/\D/g, '');
                              setForm(prev => ({ ...prev, mobile: val }));
                            }}
                            placeholder="50xxxxxxx"
                            required
                            maxLength={9}
                            pattern="^[0-9]{9}$"
                            title="Please enter a valid 9 Digit UAE mobile number starting with 50, 52, 54, 55, 56, 58 etc."
                            disabled={!!selectedOrderData.Mobile_Number}
                          />
                        </div>
                      </div>
                      <div className="mb-3">
                        <label className="form-label">ALTERNATE MOBILE NUMBER</label>
                        <div className="input-group">
                          <span className="input-group-text">+971</span>
                          <input
                            type="text"
                            className="form-control"
                            name="alternateMobile"
                            value={form.alternateMobile}
                            onChange={(e) => {
                              const val = e.target.value.replace(/\D/g, '');
                              setForm(prev => ({ ...prev, alternateMobile: val }));
                            }}
                            placeholder="50xxxxxxx"
                            maxLength={9}
                            pattern="^[0-9]{9}$"
                            title="Please enter a valid 9 Digit UAE mobile number starting with 50, 52, 54, 55, 56, 58 etc."
                          />
                        </div>
                      </div>
                      <div className="mb-3">
                        <label className="form-label">EMAIL</label>
                        <input
                          type="email"
                          className="form-control"
                          name="email"
                          value={form.email}
                          onChange={handleChange}
                          placeholder="Enter email address"
                          disabled={!!selectedOrderData.email}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Right Column: Order Details */}
                  <div className="col-md-6">
                    <div className="info-card order">
                      <div className="card-title justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                          <i className="bx bx-receipt me-2"></i>Order Details
                        </div>
                        <div className="d-flex flex-column align-items-end">
                          <span className="text-muted" style={{ fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.5px' }}>CREATED DATE</span>
                          <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#2d3748' }}>{selectedOrderData.created_date || '-'}</span>
                        </div>
                      </div>
                      <div className="row g-3">
                        <div className="col-6">
                          <div className="text-label-sm">PRODUCT</div>
                          <div className="text-value-lg">{selectedOrderData.product_name || '-'}</div>
                        </div>
                        <div className="col-6">
                          <div className="text-label-sm">SO NUMBER</div>
                          <div className="text-value-lg" style={{ color: '#276749' }}>{selectedOrderData.so_no || '-'}</div>
                        </div>
                        <div className="col-6">
                          <div className="text-label-sm">STORE</div>
                          <div className="text-value-lg">{selectedOrderData.store || '-'}</div>
                        </div>
                        <div className="col-6">
                          <div className="text-label-sm">CATEGORY</div>
                          <div className="text-value-lg">{selectedOrderData.Budget_Category || '-'}</div>
                        </div>
                        <div className="col-6">
                          <div className="text-label-sm">SUB GROUP</div>
                          <div className="text-value-lg">{selectedOrderData.Sub_Group || '-'}</div>
                        </div>
                        <div className="col-6">
                          <div className="text-label-sm">SKU</div>
                          <div className="text-value-lg">{selectedOrderData.item_code || '-'}</div>
                        </div>
                        <div className="col-6">
                          {/* IF MODEL NO IS NOT PRESENT THEN SHOW INPUT ELSE SHOW MODEL NO */}
                          <div className="text-label-sm">
                            MODEL NO {!selectedOrderData.model && <span className="text-danger">*</span>}
                          </div>
                          {selectedOrderData.model ? (
                            <div className="text-value-lg">{selectedOrderData.model}</div>
                          ) : (
                            <select
                              className="form-control"
                              name="modelNo"
                              value={form.modelNo}
                              onChange={handleChange}
                              required
                            >
                              <option value="">Select model no</option>
                              {models.map((modelName, index) => (
                                <option key={index} value={modelName}>
                                  {modelName}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                        <div className="col-6">
                          <div className="text-label-sm">SERIAL NO</div>
                          {selectedOrderData.serial_no ? (
                            <div className="text-value-lg">{selectedOrderData.serial_no}</div>
                          ) : (
                            <input
                              type="text"
                              className="form-control"
                              name="serialNo"
                              value={form.serialNo}
                              onChange={handleChange}
                              placeholder="Enter serial number"
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Address Details Accordion */}
              <div className="mb-4">
                <div
                  className={`address-accordion-header ${isOpenAddress ? 'open' : ''}`}
                  onClick={() => setIsOpenAddress(!isOpenAddress)}
                >
                  <div className="d-flex align-items-center">
                    <i className="bx bx-map-pin me-2" style={{ fontSize: '1.25rem' }}></i>
                    <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>Address Details</span>
                  </div>
                  <i className={`bx ${isOpenAddress ? 'bx-chevron-up' : 'bx-chevron-down'}`} style={{ fontSize: '1.25rem' }}></i>
                </div>
                <Collapse isOpen={isOpenAddress}>
                  <div className="address-accordion-body mt-2 border rounded-3 bg-white shadow-sm overflow-hidden">
                    <AddAddress
                      service_order_no={null}
                      showmapandAddress={true}
                      setShowmapandAddress={() => {}}
                      source="serviceOrder"
                      setAddress={handleAddressSet}
                      hideSubmit={true}
                      formRef={addressFormRef}
                      initialData={initialAddressData}
                      country="ae"
                    />
                  </div>
                </Collapse>
              </div>
              </>
            ) : (
              <div className="row">
                <div className="mb-3 col-4">
                  <label className="form-label">Fullname</label>
                  <input
                    type="text"
                    className="form-control"
                    name="fullname"
                    value={form.fullname}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="mb-3 col-4">
                  <label className="form-label">Mobile Number</label>
                  <input
                    type="text"
                    className="form-control"
                    name="mobile"
                    value={form.mobile}
                    onChange={handleChange}
                    required
                    disabled={!!form.mobile}
                  />
                </div>
                <div className="mb-3 col-4">
                  <label className="form-label">Alternate Mobile Number</label>
                  <input
                    type="text"
                    className="form-control"
                    name="alternateMobile"
                    value={form.alternateMobile}
                    onChange={handleChange}
                  />
                </div>
                <div className="mb-3 col-4">
                  <label className="form-label">Email ID</label>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            )}
            
            {/* Service Request Form - Always visible */}
            <div className="row">
              {/* Only show this Order Details input section if NOT coming from search */}
              {!selectedOrderData && (
                <>
                  <label className={`form-label`}>Order Details</label>
              <div className={`mb-3 col-12`}>
                {form.orderNo &&
                customerData &&
                customerData.so_no === form.orderNo ? (
                  <div
                    className="rounded p-3 shadow-sm"
                    style={{
                      minHeight: 120,
                      background: '#e6f9ed', // light green
                      border: '2px solid #218838', // dark green
                    }}
                  >
                    <div className="d-flex align-items-center mb-2">
                      <i
                        className="bx bx-receipt me-2"
                        style={{ fontSize: 20, color: '#007bff' }}
                      ></i>
                      <span>
                        <strong>Order No:</strong> {customerData?.so_no || '-'}
                      </span>
                    </div>
                    <div className="row mb-2">
                      <div className="col-3 d-flex align-items-center mb-1">
                        <i
                          className="bx bx-calendar me-2"
                          style={{ fontSize: 18, color: '#6c757d' }}
                        ></i>
                        <span>
                          <strong>Date:</strong>{' '}
                          {customerData?.purchase_date
                            ? new Date(
                                customerData.purchase_date,
                              ).toLocaleDateString()
                            : 'N/A'}
                        </span>
                      </div>
                      <div className="col-3 d-flex align-items-center mb-1">
                        <i
                          className="bx bx-store me-2"
                          style={{ fontSize: 18, color: '#6c757d' }}
                        ></i>
                        <span>
                          <strong>Store:</strong> {customerData?.store || '-'}
                        </span>
                      </div>
                      <div className="col-3 d-flex align-items-center mb-1">
                        <i
                          className="bx bx-category me-2"
                          style={{ fontSize: 18, color: '#6c757d' }}
                        ></i>
                        <span>
                          <strong>Category:</strong>{' '}
                          {customerData?.Budget_Category || '-'}
                        </span>
                      </div>
                      <div className="col-3 d-flex align-items-center mb-1">
                        <i
                          className="bx bx-layer me-2"
                          style={{ fontSize: 18, color: '#6c757d' }}
                        ></i>
                        <span>
                          <strong>Sub Group:</strong>{' '}
                          {customerData?.Sub_Group || '-'}
                        </span>
                      </div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-6 d-flex align-items-center mb-1">
                        <i
                          className="bx bx-cube me-2"
                          style={{ fontSize: 18, color: '#6c757d' }}
                        ></i>
                        <span>
                          <strong>Product:</strong>{' '}
                          {customerData?.product_name || '-'}
                        </span>
                      </div>
                      <div className="col-6 d-flex align-items-center mb-1">
                        <i
                          className="bx bx-barcode me-2"
                          style={{ fontSize: 18, color: '#6c757d' }}
                        ></i>
                        <span>
                          <strong>Item Code:</strong>{' '}
                          {customerData?.item_code || '-'}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <label className="form-label">Order No</label>
                    <input
                      type="text"
                      className="form-control"
                      name="orderNo"
                      value={form.orderNo}
                      onChange={handleChange}
                    />
                  </>
                )}
              </div>
              <div className="mb-3 col-md-6">
                <label className="form-label">Serial Number</label>
                <input
                  type="text"
                  className="form-control"
                  name="serialNo"
                  value={form.serialNo}
                  onChange={handleChange}
                  placeholder="Enter serial number"
                />
              </div>
                </>
              )}
              <div className="mb-3 col-md-6">
                <label className="form-label">Installation Type</label>
                <div className="service-type-grid">
                  {SERVICE_REQUEST_TYPES.map((type) => (
                    <div
                      key={type}
                      className={`service-type-card ${
                        form.type === type ? 'selected' : ''
                      }`}
                      onClick={() =>
                        setForm((prev) => ({
                          ...prev,
                          type,
                          maintenanceType: '',
                        }))
                      }
                    >
                      <div className="icon-wrapper">
                        <i className={getIconClass(type)} style={{ fontSize: 24 }}></i>
                      </div>
                      <div className="label">{type}</div>
                      {form.type === type && (
                        <div className="check-indicator">
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div
                className={`mb-3 col-12 ${form.type ? '' : 'd-none'}`}
              >
                <label className="form-label">Sub Type</label>
                <div className="service-type-grid">
                  {(form.type === 'Water Purifier'
                    ? SERVICE_REQUEST_WATER_PURIFIER
                    : form.type === 'Delayed Delivery'
                    ? COMPLAINT_DELIVERY_DELAY_SUB_TYPES
                    : form.type === 'Installation Issue'
                    ? COMPLAINT_INSTALLATION_ISSUE_SUB_TYPES
                    : form.type === 'Others'
                    ? COMPLAINT_DELIVERY_OTHERS_SUB_TYPES
                    : []
                  ).map((subType) => (
                    <div
                      key={subType}
                      className={`service-type-sub-card ${
                        form.maintenanceType === subType ? 'selected' : ''
                      }`}
                      onClick={() =>
                        setForm((prev) => ({
                          ...prev,
                          maintenanceType: subType,
                        }))
                      }
                    >
                      <div className="icon-wrapper">
                        <i className={getIconClass(subType)} style={{ fontSize: 24 }}></i>
                      </div>
                      <div className="label">{subType}</div>
                      {form.maintenanceType === subType && (
                        <div className="check-indicator">
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              {/* <div
                className={`mb-3 col-4 ${
                  form.serviceRequest &&
                  form.type === 'Water Purifier' &&
                  form.maintenanceType
                    ? ''
                    : 'd-none'
                }`}
              >
                <label className="form-label">Number of Qty</label>
                <input
                  type="number"
                  className="form-control"
                  name="qty"
                  value={form.qty}
                  onChange={handleChange}
                  min="1"
                />
              </div> */}
              <div className="mb-3 col-12">
                <label className="form-label">Remarks</label>
                <textarea
                  className="form-control"
                  name="remarks"
                  value={form.remarks}
                  onChange={handleChange}
                  rows={3}
                />
              </div>
              <div className="mb-3 col-12">
                <label className="form-label">Upload Files</label>
                <div
                  {...getRootProps()}
                  className={`dropzone-container ${isDragActive ? 'active' : ''}`}
                >
                  <input {...getInputProps()} />
                  <i className="bx bx-cloud-upload upload-icon"></i>
                  {isDragActive ? (
                    <div className="upload-text">Drop the files here...</div>
                  ) : (
                    <>
                      <div className="upload-text">
                        Drag & drop files here, or click to select files
                      </div>
                      <div className="upload-hint">
                        Only *.jpeg and *.png images will be accepted (Max 5 files)
                      </div>
                    </>
                  )}
                </div>

                {fileError && (
                  <div className="text-danger mt-1" style={{ fontSize: 13 }}>
                    {fileError}
                  </div>
                )}
                {form.files && form.files.length > 0 && (
                  <ul className="list-group mt-2">
                    {Array.from(form.files).map((file, idx) => (
                      <li
                        className="list-group-item py-1 px-2 d-flex align-items-center justify-content-between"
                        key={idx}
                        style={{ fontSize: 14 }}
                      >
                        <span>
                          <i
                            className="bx bx-paperclip"
                            style={{ marginRight: 6 }}
                          ></i>
                          {file.name}{' '}
                          <span style={{ color: '#888', fontSize: 12 }}>
                            ({(file.size / 1024).toFixed(1)} KB)
                          </span>
                        </span>
                        <button
                          type="button"
                          className="btn btn-sm btn-link text-danger ms-2"
                          title="Remove file"
                          style={{ textDecoration: 'none', fontSize: 18 }}
                          onClick={() => {
                            setForm((prev) => {
                              const newFiles = prev.files.filter(
                                (_, i) => i !== idx,
                              );
                              return { ...prev, files: newFiles };
                            });
                            setFileError('');
                          }}
                        >
                          <i className="bx bx-x"></i>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="mb-0 col-12">
                <button
                  type="submit"
                  className="btn-submit d-flex align-items-center justify-content-center gap-2"
                  style={{ width: 'auto' }}
                  disabled={isDataLoading}
                >
                  {isDataLoading ? (
                    <>
                      <Spinner size="sm" color="light" />
                      <span>Creating...</span>
                    </>
                  ) : (
                    <span>Create Service Order</span>
                  )}
                </button>
              </div>
            </div>
          </form>
        
      </OffcanvasBody>
    </Offcanvas>
  );
};

export default CreateServiceOrderOffcanvas;
